// src/pages/CourseTime/components/PollResultsChart.comp.jsx
import React from 'react';
import * as S from '../styles';
import { GET_APP_LOCALIZE_TEXT } from '../../../utils/dq_lc_service/lc_service';

const PollResultsChart = ({ pollData, pageLc }) => {
  if (!pollData || !pollData.options || pollData.options.length === 0) {
    return (
      <S.ChartContainer>
        <S.NoDataMessage>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__no_poll_results_available')}</S.NoDataMessage>
      </S.ChartContainer>
    );
  }

  // Generate colors for pie chart segments
  const generateColors = (count) => {
    const baseColors = [
      '#7395cc', // dark blue
      '#3ac5f3', // light blue
      '#e7e03b', // yellow
      '#b1cc3d', // green
      '#eeac47', // orange
      '#eb6327', // dark orange
      '#bc2268', // dark magenta
      '#da4b90', // Pink
      '#018fa6', // Teal
      '#000000'  // black
    ];
    
    // Repeat colors if we have more options than base colors
    return Array.from({ length: count }, (_, i) => baseColors[i % baseColors.length]);
  };

  const colors = generateColors(pollData.options.length);
  
  // Filter options with data for pie chart (exclude 0% options from chart but keep in legend)
  const optionsWithData = pollData.options.filter(option => option.percentage > 0);
  
  // Calculate cumulative percentages for creating pie segments
  let cumulativePercentage = 0;
  const chartSegments = optionsWithData.map((option, index) => {
    const startAngle = cumulativePercentage * 3.6; // Convert percentage to degrees
    const endAngle = (cumulativePercentage + option.percentage) * 3.6;
    cumulativePercentage += option.percentage;
    
    // Find original index to maintain color consistency
    const originalIndex = pollData.options.findIndex(opt => opt.text === option.text);
    
    return {
      ...option,
      color: colors[originalIndex],
      startAngle,
      endAngle,
      originalIndex
    };
  });
  
  // All segments for legend (including 0% options)
  const allSegments = pollData.options.map((option, index) => ({
    ...option,
    color: colors[index],
    index
  }));
  
  console.log('Chart segments:', chartSegments);
  console.log('All segments for legend:', allSegments);

  // Create SVG path for pie slice (for partial segments only)
  const createPieSlice = (centerX, centerY, radius, startAngle, endAngle) => {
    const startAngleRad = (startAngle - 90) * Math.PI / 180;
    const endAngleRad = (endAngle - 90) * Math.PI / 180;
    
    const x1 = centerX + radius * Math.cos(startAngleRad);
    const y1 = centerY + radius * Math.sin(startAngleRad);
    const x2 = centerX + radius * Math.cos(endAngleRad);
    const y2 = centerY + radius * Math.sin(endAngleRad);
    
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    
    return `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  };

  const chartSize = 300;
  const centerX = chartSize / 2;
  const centerY = chartSize / 2;
  const radius = 120;

  return (
    <S.ChartContainer>
      <S.ChartTitle>{pollData.question}</S.ChartTitle>
      <S.ChartSubtitle>
        {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__total_responses')}: {pollData.totalResponses} • {pollData.pollType === 'MAQ' ? GET_APP_LOCALIZE_TEXT(pageLc, 'ct__multiple_selections_allowed') : GET_APP_LOCALIZE_TEXT(pageLc, 'ct__single_selection')}
      </S.ChartSubtitle>
      
      <S.ChartWrapper>
        <S.PieChart>
          <svg width={chartSize} height={chartSize} viewBox={`0 0 ${chartSize} ${chartSize}`}>
            {chartSegments.length > 0 ? (
              chartSegments.length === 1 && chartSegments[0].percentage === 100 ? (
                // Special case: single option with 100% - render as complete circle
                <S.PieSlice
                  key={0}
                  as="circle"
                  cx={centerX}
                  cy={centerY}
                  r={radius}
                  fill={chartSegments[0].color}
                  data-option={chartSegments[0].text}
                  data-percentage={chartSegments[0].percentage}
                />
              ) : (
                // Multiple segments - render as pie slices
                chartSegments.map((segment, index) => (
                  <S.PieSlice
                    key={index}
                    d={createPieSlice(centerX, centerY, radius, segment.startAngle, segment.endAngle)}
                    fill={segment.color}
                    data-option={segment.text}
                    data-percentage={segment.percentage}
                  />
                ))
              )
            ) : (
              // Show a placeholder circle if no data
              <>
                <circle
                  cx={centerX}
                  cy={centerY}
                  r={radius}
                  fill="#f3f4f6"
                  stroke="#e5e7eb"
                  strokeWidth="2"
                />
                <text
                  x={centerX}
                  y={centerY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="16"
                  fill="#6b7280"
                >
                  {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__no_votes_yet')}
                </text>
              </>
            )}
          </svg>
        </S.PieChart>
        
        <S.ChartLegend>
          {allSegments.map((segment, index) => (
            <S.LegendItem key={index}>
              <S.LegendColor color={segment.color} />
              <S.LegendText>
                <S.LegendLabel>{segment.text}</S.LegendLabel>
                <S.LegendStats>
                  {segment.count} {segment.count === 1 ? GET_APP_LOCALIZE_TEXT(pageLc, 'ct__vote') : GET_APP_LOCALIZE_TEXT(pageLc, 'ct__votes')} ({segment.percentage.toFixed(1)}%)
                </S.LegendStats>
              </S.LegendText>
            </S.LegendItem>
          ))}
        </S.ChartLegend>
      </S.ChartWrapper>
    </S.ChartContainer>
  );
};

export default PollResultsChart;
